import { Injectable } from '@angular/core'
import {
  AppIdInput,
  CreateAppInput,
  ScopesGql,
  SuccessOutput,
  TestAppWebhookInput,
  UpdateAppInput,
} from '@papi/interfaces'
import { Apollo, gql } from 'apollo-angular'
import { merge } from 'rxjs'
import { map } from 'rxjs/operators'
import { throwGraphQLErrors } from '../graphql/graphql.utils'
import { appSchema, UsersPartnerApp } from './app.model'
import { scopeFields } from './scopes'

const CREATE_APP_MUTATION = gql`
  mutation ($input: CreateAppInput!) {
    createApp(input: $input) {
      success
    }
  }
`

const UPDATE_APP_MUTATION = gql`
  mutation ($input: UpdateAppInput!) {
    updateApp(input: $input) {
      success
    }
  }
`

const ACTIVATE_APP_MUTATION = gql`
  mutation ($input: AppIdInput!) {
    activateApp(input: $input) {
      success
    }
  }
`

const DEACTIVATE_APP_MUTATION = gql`
  mutation ($input: AppIdInput!) {
    deactivateApp(input: $input) {
      success
    }
  }
`

const DELETE_APP_MUTATION = gql`
  mutation ($input: AppIdInput!) {
    deleteApp(input: $input) {
      success
    }
  }
`

const TEST_APP_WEBHOOK_MUTATION = gql`
  mutation ($input: TestAppWebhookInput!) {
    testAppWebhook(input: $input) {
      success
    }
  }
`

const CREATED_SUBSCRIPTION = gql`
  subscription {
    appCreated ${appSchema}
  }
`

const UPDATED_SUBSCRIPTION = gql`
  subscription {
    appUpdated ${appSchema}
  }
`

const DELETED_SUBSCRIPTION = gql`
  subscription {
    appDeleted {
      _id
    }
  }
`

const GET_APP_SCOPES_QUERY = gql`
  query getPartner($partnerId: String!, $appId: String!) {
    getPartner(id: $partnerId) {
      app(id: $appId){
        scopes {
          ${scopeFields}
        }
      }
    }
  }
`

@Injectable()
export class AppsService {
  constructor(private apollo: Apollo) {}

  fetchAppScopes(partnerId: string, appId: string) {
    return this.apollo
      .query<{ getPartner: { app: { scopes: ScopesGql } } }>({
        query: GET_APP_SCOPES_QUERY,
        variables: {
          partnerId,
          appId,
        },
      })
      .pipe(map(({ data }) => data.getPartner.app.scopes))
  }

  createApp(input: CreateAppInput) {
    return this.apollo
      .mutate<{ createApp: SuccessOutput }>({ mutation: CREATE_APP_MUTATION, variables: { input } })
      .pipe(
        map(throwGraphQLErrors),
        map((res) => res.data.createApp)
      )
  }

  updateApp(input: UpdateAppInput) {
    return this.apollo
      .mutate<{ updateApp: SuccessOutput }>({ mutation: UPDATE_APP_MUTATION, variables: { input } })
      .pipe(
        map(throwGraphQLErrors),
        map((res) => res.data.updateApp)
      )
  }

  activateApp(input: AppIdInput) {
    return this.apollo
      .mutate<{ activateApp: SuccessOutput }>({ mutation: ACTIVATE_APP_MUTATION, variables: { input } })
      .pipe(
        map(throwGraphQLErrors),
        map((res) => res.data.activateApp)
      )
  }

  deactivateApp(input: AppIdInput) {
    return this.apollo
      .mutate<{ deactivateApp: SuccessOutput }>({ mutation: DEACTIVATE_APP_MUTATION, variables: { input } })
      .pipe(
        map(throwGraphQLErrors),
        map((res) => res.data.deactivateApp)
      )
  }

  deleteApp(input: AppIdInput) {
    return this.apollo
      .mutate<{ deleteApp: SuccessOutput }>({ mutation: DELETE_APP_MUTATION, variables: { input } })
      .pipe(
        map(throwGraphQLErrors),
        map((res) => res.data.deleteApp)
      )
  }

  testWebhook(input: TestAppWebhookInput) {
    return this.apollo
      .mutate<{ testAppWebhook: SuccessOutput }>({
        mutation: TEST_APP_WEBHOOK_MUTATION,
        variables: { input },
      })
      .pipe(
        map(throwGraphQLErrors),
        map((res) => res.data.testAppWebhook)
      )
  }

  listenForAppsDeletion() {
    return this.apollo
      .subscribe<{ appDeleted: UsersPartnerApp }>({ query: DELETED_SUBSCRIPTION })
      .pipe(map((res) => res.data.appDeleted))
  }

  listenForAppsChanges() {
    return merge(
      this.apollo
        .subscribe<{ appCreated: UsersPartnerApp }>({ query: CREATED_SUBSCRIPTION })
        .pipe(map((res) => res.data.appCreated)),
      this.apollo
        .subscribe<{ appUpdated: UsersPartnerApp }>({ query: UPDATED_SUBSCRIPTION })
        .pipe(map((res) => res.data.appUpdated))
    )
  }
}
